import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authErrorMessage } from '../lib/authErrors';
import AuthLayout, { authLinkClass } from '../components/AuthLayout';
import PasswordChangeForm from '../components/PasswordChangeForm';

/**
 * Handles both the forced first-login change (users.last_login = null, see ProtectedRoute)
 * and the recovery link flow (Supabase opens a session and emits PASSWORD_RECOVERY).
 */
export default function ResetPassword() {
  const { session, userProfile, userRole, loading, isPasswordRecovery, changePassword, completePasswordChange } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <AuthLayout title="Restablecer contraseña">
        <div className="flex justify-center py-6" role="status" aria-label="Cargando">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-hidden="true" />
        </div>
      </AuthLayout>
    );
  }

  if (!session) {
    return (
      <AuthLayout
        title="El enlace no es válido o expiró"
        description="Para cambiar tu contraseña necesitas un enlace de recuperación vigente."
        footer={
          <Link to="/login" className={authLinkClass}>
            Volver a iniciar sesión
          </Link>
        }
      >
        <Link to="/forgot-password" className={`text-sm ${authLinkClass}`}>
          Solicitar un nuevo enlace
        </Link>
      </AuthLayout>
    );
  }

  const isFirstLogin = userProfile?.last_login === null;
  const home = userRole === 'admin' || userRole === 'reviewer' ? '/admin/dashboard' : '/';

  const description = isFirstLogin
    ? 'Por seguridad, cambia la contraseña temporal que recibiste antes de continuar.'
    : isPasswordRecovery
      ? 'Elige una nueva contraseña para tu cuenta.'
      : 'Ingresa una nueva contraseña para tu cuenta.';

  const handleSubmit = async (password: string) => {
    const { error } = await changePassword(password);
    if (error) {
      console.error('Password change error:', error);
      return authErrorMessage(error);
    }
    // Clears the first-login flag and reloads the profile before leaving,
    // so ProtectedRoute does not bounce the user back here.
    await completePasswordChange();
    navigate(home, { replace: true });
    return null;
  };

  return (
    <AuthLayout title={isFirstLogin ? 'Crea tu contraseña' : 'Cambiar contraseña'} description={description}>
      <PasswordChangeForm
        onSubmit={handleSubmit}
        submitLabel="Guardar y continuar"
        secondaryAction={
          !isFirstLogin && (
            <Link
              to={home}
              className="flex w-full justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </Link>
          )
        }
      />
    </AuthLayout>
  );
}
